import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Zap, Brain, Heart, Target, ChevronRight, Sparkles } from 'lucide-react';
import { useRef } from 'react';

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const fadeUp = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const pillars = [
    { icon: Zap, label: 'Énergie', description: 'Stabilité et vitalité au quotidien' },
    { icon: Brain, label: 'Mental', description: 'Clarté et focus durables' },
    { icon: Heart, label: 'Récupération', description: 'Repos et régénération optimisés' },
    { icon: Target, label: 'Habitudes', description: 'Routines alignées avec votre vie' },
  ];

  const differentiators = [
    'Pas de solutions miracles',
    'Pas de programmes standards',
    'Places volontairement limitées',
    'Résultats mesurables',
  ];

  return (
    <div data-testid="home-page" className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <motion.div 
            className="hero-gradient absolute inset-0 z-10"
            animate={{ 
              background: [
                'radial-gradient(ellipse at 30% 40%, rgba(143, 188, 143, 0.12) 0%, rgba(5, 5, 5, 0) 60%)',
                'radial-gradient(ellipse at 70% 60%, rgba(152, 212, 187, 0.12) 0%, rgba(5, 5, 5, 0) 60%)',
                'radial-gradient(ellipse at 30% 40%, rgba(143, 188, 143, 0.12) 0%, rgba(5, 5, 5, 0) 60%)',
              ]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.img
            style={{ y: heroY }}
            src="https://images.unsplash.com/photo-1630761935890-0aef87fd7e8f?auto=format&fit=crop&w=2000&q=80"
            alt="Silhouette au lever du soleil"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
            onLoad={(e) => e.target.classList.add('loaded')}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]" />
        </div>

        {/* Floating elements */}
        <motion.div
          className="absolute top-1/4 left-[10%] w-32 h-32 rounded-full bg-sage/5 blur-3xl"
          animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/3 right-[15%] w-48 h-48 rounded-full bg-mint/5 blur-3xl"
          animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/10 border border-sage/20 mb-8"
            >
              <Sparkles size={16} className="text-sage" />
              <span className="font-body text-sm tracking-wide text-sage">
                Accompagnement Premium
              </span>
            </motion.div>
            
            <motion.h1 
              className="font-heading text-5xl md:text-7xl lg:text-8xl font-medium text-white leading-[1.1] mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Santé, énergie
              <br />
              <span className="text-sage">performance durable</span>
            </motion.h1>
            
            <motion.p 
              className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-12 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Une méthodologie de transformation globale pour ceux qui veulent 
              des résultats concrets, durables et alignés avec leur vraie vie.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="bg-sage text-black font-body font-semibold px-8 py-4 rounded-full hover:bg-sage-300 transition-all duration-300 flex items-center gap-2 shimmer"
                >
                  Planifier un premier échange
                  <ArrowRight size={20} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/particuliers"
                  data-testid="hero-cta-secondary"
                  className="bg-transparent border border-white/20 text-white font-body font-medium px-8 py-4 rounded-full hover:bg-white/5 hover:border-white/40 transition-all duration-300"
                >
                  Découvrir l'accompagnement
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div 
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
            animate={{ borderColor: ['rgba(255,255,255,0.3)', 'rgba(143,188,143,0.5)', 'rgba(255,255,255,0.3)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1.5 h-1.5 bg-sage rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Entry Points Section */}
      <section className="py-24 md:py-32 bg-[#050505] relative">
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-sage/3 blur-[150px]"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Votre accompagnement
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
              Choisissez votre parcours
            </h2>
            <p className="font-body text-gray-400 max-w-xl mx-auto">
              Un accompagnement structuré et individualisé, adapté à votre contexte.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {/* B2C Card */}
            <motion.div variants={staggerItem}>
              <Link
                to="/particuliers"
                data-testid="entry-point-b2c"
                className="group block h-full"
              >
                <motion.div 
                  className="relative h-full rounded-3xl overflow-hidden bg-[#0F0F0F] border border-white/5 hover:border-sage/30 transition-all duration-500"
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0">
                    <img
                      src="https://images.unsplash.com/photo-1604938814491-c696899ec59b?auto=format&fit=crop&w=1200&q=80"
                      alt="Équilibre zen"
                      className="w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-110 transition-all duration-700"
                      onLoad={(e) => e.target.classList.add('loaded')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent" />
                  </div>
                  <div className="relative p-8 md:p-12 h-full flex flex-col justify-end min-h-[420px]">
                    <motion.span 
                      className="font-body text-sm tracking-[0.15em] uppercase text-sage mb-4"
                      whileHover={{ x: 5 }}
                    >
                      Particuliers
                    </motion.span>
                    <h3 className="font-heading text-3xl md:text-4xl text-white mb-4">
                      Je suis un particulier
                    </h3>
                    <p className="font-body text-gray-400 mb-6 max-w-md">
                      Retrouvez de l'énergie, plus de clarté mentale, une transformation 
                      physique et une performance durable sans bouleverser votre quotidien.
                    </p>
                    <div className="flex items-center gap-2 text-sage font-body font-medium group-hover:gap-4 transition-all duration-300">
                      Découvrir
                      <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>

            {/* B2B Card */}
            <motion.div variants={staggerItem}>
              <Link
                to="/entreprises"
                data-testid="entry-point-b2b"
                className="group block h-full"
              >
                <motion.div 
                  className="relative h-full rounded-3xl overflow-hidden bg-[#0F0F0F] border border-white/5 hover:border-sage/30 transition-all duration-500"
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0">
                    <img
                      src="https://images.unsplash.com/photo-1768269378478-2c48665c0e51?auto=format&fit=crop&w=1200&q=80"
                      alt="Architecture moderne"
                      className="w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-110 transition-all duration-700"
                      onLoad={(e) => e.target.classList.add('loaded')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent" />
                  </div>
                  <div className="relative p-8 md:p-12 h-full flex flex-col justify-end min-h-[420px]">
                    <motion.span 
                      className="font-body text-sm tracking-[0.15em] uppercase text-sage mb-4"
                      whileHover={{ x: 5 }}
                    >
                      Entreprises
                    </motion.span>
                    <h3 className="font-heading text-3xl md:text-4xl text-white mb-4">
                      Je représente une entreprise
                    </h3>
                    <p className="font-body text-gray-400 mb-6 max-w-md">
                      Améliorez l'énergie et le mental de vos collaborateurs. 
                      Performance durable plutôt que bien-être ponctuel.
                    </p>
                    <div className="flex items-center gap-2 text-sage font-body font-medium group-hover:gap-4 transition-all duration-300">
                      Découvrir
                      <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
        <motion.div
          className="absolute -right-32 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-mint/5 blur-[100px]"
          animate={{ x: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="mb-16">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
              Notre approche
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-6 max-w-2xl">
              Les leviers de la performance durable
            </h2>
            <p className="font-body text-gray-400 max-w-xl">
              Ce qui influence réellement votre fonctionnement au quotidien.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.label}
                variants={staggerItem}
                whileHover={{ y: -10, scale: 1.02 }}
                className="p-8 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500 group"
              >
                <motion.div 
                  className="w-14 h-14 rounded-xl bg-sage/10 flex items-center justify-center mb-6 group-hover:bg-sage/20 transition-colors"
                  whileHover={{ rotate: 5 }}
                >
                  <pillar.icon className="text-sage" size={28} />
                </motion.div>
                <h3 className="font-heading text-2xl text-white mb-3">
                  {pillar.label}
                </h3>
                <p className="font-body text-gray-400">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Engagement Section */}
      <section className="py-24 md:py-32 bg-[#050505] relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp}>
              <span className="font-body text-sm tracking-[0.2em] uppercase text-sage mb-4 block">
                Un réel engagement
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
                Un accompagnement <span className="text-sage">exigeant</span>
              </h2>
              <p className="font-body text-gray-400 mb-8 leading-relaxed">
                EasyCoaching ne vend pas "du coaching". Nous proposons une méthodologie 
                de transformation globale pour ceux qui sont prêt(e)s à s'engager 
                dans une démarche structurée et mesurable.
              </p>
              
              <motion.ul 
                className="space-y-4 mb-10"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {differentiators.map((item, index) => (
                  <motion.li
                    key={item}
                    variants={staggerItem}
                    className="flex items-center gap-4"
                  >
                    <motion.span 
                      className="w-2.5 h-2.5 rounded-full bg-sage"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
                    />
                    <span className="font-body text-white">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>

              <motion.p 
                className="font-body text-sm text-gray-500 italic border-l-2 border-sage/50 pl-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                "Je n'accompagne pas tout le monde. Un accompagnement demande un réel engagement – des deux côtés."
              </motion.p>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <motion.div 
                className="relative rounded-3xl overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1614691048047-17b747e3aee9?auto=format&fit=crop&w=1200&q=80"
                  alt="Silhouette montagne lever de soleil"
                  className="w-full h-[500px] object-cover"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
              </motion.div>
              
              {/* Stats overlay */}
              <motion.div 
                className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <motion.span 
                      className="font-heading text-3xl text-sage"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 }}
                    >
                      100%
                    </motion.span>
                    <p className="font-body text-sm text-gray-400 mt-1">Personnalisé</p>
                  </div>
                  <div>
                    <motion.span 
                      className="font-heading text-3xl text-sage"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.7 }}
                    >
                      Long terme
                    </motion.span>
                    <p className="font-body text-sm text-gray-400 mt-1">Partenariat</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          animate={{ 
            background: [
              'radial-gradient(circle at 30% 50%, rgba(143, 188, 143, 0.1) 0%, rgba(10, 10, 10, 0) 50%)',
              'radial-gradient(circle at 70% 50%, rgba(152, 212, 187, 0.1) 0%, rgba(10, 10, 10, 0) 50%)',
              'radial-gradient(circle at 30% 50%, rgba(143, 188, 143, 0.1) 0%, rgba(10, 10, 10, 0) 50%)',
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Prêt(e) à vous engager ?
            </h2>
            <p className="font-body text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
              Un premier échange de 30 minutes pour analyser votre situation 
              et déterminer si nous pouvons travailler ensemble.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/contact"
                data-testid="cta-final-button"
                className="inline-flex items-center gap-3 bg-sage text-black font-body font-semibold px-10 py-5 rounded-full hover:bg-sage-300 transition-all duration-300 text-lg pulse-glow"
              >
                Planifier un premier échange
                <ArrowRight size={22} />
              </Link>
            </motion.div>
            <p className="font-body text-sm text-gray-500 mt-6">
              Réponse sous 24h • Sans engagement
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
